import React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { BrandContext } from "../../App";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../../hooks/useAPI";

import TotalScoreCard from "../../components/complex/TotalScoreCard";
import OtherScoresCard from "../../components/complex/OtherScoresCard";
import DefaultButton from "../../components/simple/DefaultButton";
import PoweredBy from "../../assets/logos/powered-by.png";
import Logo from "../../assets/logos/2x-logo-light.png";

export function ExternalScenarioFeedback() {
  const theme = useTheme();
  const { brand, user, authToken } = React.useContext(BrandContext);
  const { callAPI } = useAPI();
  const [simulation, setSimulation] = React.useState(null);
  const [emailModalOpen, setEmailModalOpen] = React.useState(true);
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const navigate = useNavigate();

  const { simulation_id } = useParams();

  React.useEffect(() => {
    if (simulation && simulation.evaluation === null) {
      const interval = setInterval(() => {
        console.log("Checking for evaluation update...");
        updateSimulationData();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [simulation]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "name") {
      setName(value);
    }
    if (name === "email") {
      setEmail(value);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    callAPI("leads", {
      method: "POST",
      body: { name, email },
    })
      .then((data) => {
        setEmailModalOpen(false);
      })
      .catch((err) => {
        console.error("Submission error:", err.message);
      });
  };

  function updateSimulationData() {
    callAPI(`external_simulation?simulation_id=${simulation_id}`, {
      token: authToken,
    })
      .then((data) => {
        setSimulation(data);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }

  React.useEffect(() => {
    document.title = `Simulation Feedback - ${brand.name}`;
  }, [user]);

  React.useEffect(() => {
    updateSimulationData();
  }, [authToken]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.primary,
        height: "100vh",
      }}
    >
      <Modal
        open={emailModalOpen}
        onSubmit={handleSubmit}
        onChange={handleInputChange}
        disableEscapeKeyDown
        disableBackdropClick
        hideBackdrop={false}
        sx={{
          backdropFilter: "blur(30px)",
          backgroundColor: "rgba(0, 0, 0, 0.2)", // semi-transparent black overlay
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: theme.palette.background.brand_primary,
            boxShadow: 24,
            borderRadius: theme.shape.radius,
            p: theme.customSpacing["3xl"],
            textAlign: "center",
          }}
        >
          <img
            src={Logo}
            alt="GuruNow Logo"
            style={{ width: 67, marginBottom: theme.customSpacing.xl }}
          />
          <Typography
            sx={{
              mb: 1,
              fontSize: theme.typography.textLG.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: theme.typography.textLG.lineHeight,
              color: theme.palette.text.brand_primary,
            }}
          >
            Please enter your email
          </Typography>
          <Typography
            sx={{
              mb: theme.customSpacing.lg,
              fontSize: theme.typography.textSM.fontSize,
              fontWeight: theme.typography.fontWeightLight,
              lineHeight: theme.typography.textSM.lineHeight,
              color: theme.palette.foreground.tertiary,
            }}
          >
            To receive your results please enter your email address!
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Your name"
              name="name"
              variant="outlined"
              fullWidth
              required
              value={name}
              onChange={handleInputChange}
              sx={{
                mb: theme.customSpacing.xl,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: theme.palette.border.disabled,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.border.disabled,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.border.disabled,
                  },
                  "& input::placeholder": {
                    color: theme.palette.text.placeholder,
                    opacity: 1,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme.palette.text.placeholder,
                },
                "& .MuiInputBase-input": {
                  color: theme.palette.text.secondary,
                },
              }}
            />
            <TextField
              label="Work email"
              name="email"
              type="email"
              variant="outlined"
              fullWidth
              required
              value={email}
              onChange={handleInputChange}
              sx={{
                mb: theme.customSpacing.xl,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: theme.palette.border.disabled,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.border.disabled,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.border.disabled,
                  },
                  "& input::placeholder": {
                    color: theme.palette.text.placeholder,
                    opacity: 1,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme.palette.text.placeholder,
                },
                "& .MuiInputBase-input": {
                  color: theme.palette.text.secondary,
                },
              }}
            />
            <DefaultButton variant="contained" fullWidth type="submit">
              Get started
            </DefaultButton>
          </form>
        </Box>
      </Modal>
      {simulation == null || simulation.evaluation == null ? (
        <>
          <Typography variant="h4">
            Please wait while we tailor your personalised feedback
          </Typography>
          {simulation?.scenario?.id && (
            <Button
              href={`/externalscenario/${simulation.scenario.id}`}
              sx={{ mt: 2 }}
            >
              Back to scenario
            </Button>
          )}
        </>
      ) : (
        <Box sx={{ padding: theme.containers.paddingDesktop, height: "80vh" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "start",
              justifyContent: "space-between",
              mb: theme.customSpacing["3xl"],
            }}
          >
            <Box>
              <Typography variant="h4">
                Take a look below at your scores{" "}
                {simulation?.user?.first_name || null}
              </Typography>
              <Typography
                sx={{
                  fontStyle: "italic",
                  fontWeight: theme.typography.fontWeightLight,
                  fontSize: theme.typography.textMD.fontSize,
                }}
              >
                See how you did, and where you can improve!
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: theme.customSpacing["lg"],
              }}
            >
              <DefaultButton
                onClick={() =>
                  navigate(`/externalscenario/${simulation.scenario.id}`)
                }
              >
                Try Again
              </DefaultButton>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              height: "100%",
              gap: theme.customSpacing["2xl"],
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TotalScoreCard theme={theme} simulation={simulation} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <OtherScoresCard theme={theme} simulation={simulation} />
            </Box>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          position: "absolute",
          bottom: theme.customSpacing.xs,
          right: theme.containers.paddingDesktop,
          width: "7.5vh",
        }}
      >
        <img
          src={PoweredBy}
          alt="Powered by"
          style={{ width: "100%", height: "auto" }}
        />
      </Box>
    </Box>
  );
}
